import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronsUpDown, Check, Search, History, ScanBarcode, Car, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { eslesir, Vurgu, sonSecilenler, sonGetir, sonaEkle, ayniKod } from "@/lib/ara";
import { useAuth } from "@/context/AuthContext";

const GOSTER = 150;
const ac = (o) => (typeof o === "object" ? { value: String(o.value), label: String(o.label) } : { value: String(o), label: String(o) });
const kodu = (o) => o.label.split(" · ")[0];

const miktaraGec = (tetik) => {
  if (!tetik) return false;
  const kap = tetik?.closest("[role=dialog]") || document;
  const sonrakiler = [...kap.querySelectorAll("input:not([type=hidden]):not([disabled])")].filter((el) => tetik.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING);
  const sonraki = sonrakiler.find((el) => el.type === "number") || sonrakiler[0];
  if (!sonraki) return false;
  sonraki.focus();
  sonraki.select?.();
  return true;
};

const AramaListesi = ({ value, onChange, options, testid, placeholder, urun, defaultOpen = false }) => {
  const { user } = useAuth() || {};
  const [acik, setAcik] = useState(defaultOpen);
  const [q, setQ] = useState("");
  const [i, setI] = useState(0);
  const liste = useRef(null);
  const tetik = useRef(null);
  const odakla = useRef(false);
  const [sonDeger, setSonDeger] = useState([]);
  const hepsi = useMemo(() => options.map(ac), [options]);
  useEffect(() => {
    if (!urun || !acik) { return; }
    setSonDeger(sonSecilenler(user?.id, "urun"));
    sonGetir(user?.id, "urun").then(setSonDeger);
  }, [urun, acik, user?.id]);
  const sonlar = useMemo(() => {
    if (!urun || !acik) return [];
    const m = new Map(hepsi.map((o) => [o.value, o]));
    return sonDeger.map((v) => m.get(v)).filter(Boolean);
  }, [urun, acik, hepsi, sonDeger]);
  const sonuc = useMemo(() => {
    if (q.trim() || !sonlar.length) return hepsi.filter((o) => eslesir(q, o.label));
    const s = new Set(sonlar.map((o) => o.value));
    return [...sonlar, ...hepsi.filter((o) => !s.has(o.value))];
  }, [hepsi, q, sonlar]);
  const sonSayi = q.trim() ? 0 : sonlar.length;
  const secili = hepsi.find((o) => o.value === String(value ?? ""));
  useEffect(() => { setI(0); }, [q]);
  useEffect(() => { liste.current?.querySelector(`[data-idx="${i}"]`)?.scrollIntoView({ block: "nearest" }); }, [i]);
  const sec = (o) => {
    onChange(o.value);
    if (urun) { sonaEkle(user?.id, "urun", o.value); odakla.current = true; }
    setAcik(false); setQ("");
  };
  const tus = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setI((x) => Math.min(x + 1, Math.min(sonuc.length, GOSTER) - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setI((x) => Math.max(x - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const tam = urun && q.trim() && hepsi.find((o) => ayniKod(kodu(o), q));
      if (tam) sec(tam);
      else if (sonuc[i]) sec(sonuc[i]);
      else if (urun && q.trim()) toast.error(`"${q.trim()}" stok koduyla ürün bulunamadı`);
    }
  };
  const satir = (o, n) => (
    <div key={o.value} role="option" aria-selected={n === i} data-idx={n} data-testid={`${testid}-opt-${o.value}`} onMouseEnter={() => setI(n)} onClick={() => sec(o)}
      className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer ${n === i ? "bg-white/10" : ""}`}>
      <Check className={`w-3.5 h-3.5 shrink-0 ${o.value === secili?.value ? "opacity-100" : "opacity-0"}`} />
      <span className="truncate"><Vurgu text={o.label} q={q} /></span>
    </div>
  );
  return (
    <Popover modal open={acik} onOpenChange={(o) => { setAcik(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <button ref={tetik} type="button" role="combobox" aria-expanded={acik} data-testid={testid}
          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-white/5 px-3 py-2 text-sm text-left">
          <span className={`truncate ${secili ? "" : "text-muted-foreground"}`}>{secili ? secili.label : placeholder}</span>
          <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px] glass" align="start"
        onCloseAutoFocus={(e) => { if (odakla.current) { odakla.current = false; if (miktaraGec(tetik.current)) e.preventDefault(); } }}>
        <div className="flex items-center gap-2 px-3 border-b border-white/10">
          {urun ? <ScanBarcode className="w-4 h-4 text-muted-foreground" /> : <Search className="w-4 h-4 text-muted-foreground" />}
          <input autoFocus data-testid={`${testid}-ara`} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={tus} placeholder={urun ? "Yazın veya barkod okutun…" : "Yazdıkça daralır…"}
            className="h-10 w-full bg-transparent text-sm outline-none" />
          <span className="text-[10px] text-muted-foreground whitespace-nowrap" data-testid={`${testid}-sayi`}>{sonuc.length}/{hepsi.length}</span>
        </div>
        <div ref={liste} role="listbox" className="max-h-72 overflow-y-auto p-1">
          {sonSayi > 0 && <div className="flex items-center gap-1.5 px-2 pt-1 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground" data-testid={`${testid}-son-baslik`}><History className="w-3 h-3" />Son seçilenler</div>}
          {sonuc.slice(0, sonSayi).map(satir)}
          {sonSayi > 0 && <div className="my-1 border-t border-white/10" />}
          {sonuc.slice(sonSayi, GOSTER).map((o, n) => satir(o, n + sonSayi))}
          {!sonuc.length && <div className="px-3 py-4 text-sm text-muted-foreground text-center">Sonuç yok</div>}
          {sonuc.length > GOSTER && <div className="px-3 py-2 text-[11px] text-muted-foreground">{sonuc.length - GOSTER} sonuç daha — yazmaya devam edin</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const BOS_SECIM = "__bos__";
export const SelectBox = ({ value, onChange, options, testid, placeholder = "Seçin", urun = false, defaultOpen = false }) => (options.length > 8 || urun
  ? <AramaListesi value={value} onChange={onChange} options={options} testid={testid} placeholder={placeholder} urun={urun} defaultOpen={defaultOpen} />
  : (
  // Radix Select boş string değerli öğeye izin vermez; "" seçeneğini gözcü değerle temsil et
  <Select value={(value ?? "") === "" && options.some((o) => (typeof o === "object" ? o.value : o) === "") ? BOS_SECIM : (value ?? "")}
    onValueChange={(v) => onChange(v === BOS_SECIM ? "" : v)}>
    <SelectTrigger data-testid={testid} className="bg-white/5"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      {options.map((o) => {
        const v = typeof o === "object" ? o.value : o;
        const l = typeof o === "object" ? o.label : o;
        return <SelectItem key={v === "" ? BOS_SECIM : v} value={v === "" ? BOS_SECIM : String(v)}>{l}</SelectItem>;
      })}
    </SelectContent>
  </Select>
));

let oneriSozu = null;
const useOneriler = () => {
  const [o, setO] = useState({ markalar: [], modeller: {} });
  useEffect(() => {
    oneriSozu = oneriSozu || api.get("/araclar/oneriler").then((r) => r.data).catch(() => { oneriSozu = null; return { markalar: [], modeller: {} }; });
    oneriSozu.then(setO);
  }, []);
  return o;
};

const AracSatir = ({ a, i, tid, set, sil, o }) => {
  const modeller = o.modeller[a.marka] || [...new Set(Object.values(o.modeller).flat())];
  const kutu = "bg-white/5 h-9 text-xs";
  return (
    <div className="grid grid-cols-2 sm:grid-cols-[1.2fr_1.4fr_80px_80px_1fr_32px] gap-1.5 items-center p-2 sm:p-0 rounded-lg bg-white/[0.03] sm:bg-transparent" data-testid={`${tid}-satir-${i}`}>
      <Input list={`${tid}-markalar`} data-testid={`${tid}-marka-${i}`} placeholder="Marka *" className={kutu} value={a.marka || ""} onChange={(e) => set(i, "marka", e.target.value)} />
      <Input list={`${tid}-modeller-${i}`} data-testid={`${tid}-model-${i}`} placeholder="Model" className={kutu} value={a.model || ""} onChange={(e) => set(i, "model", e.target.value)} />
      <datalist id={`${tid}-modeller-${i}`}>{modeller.map((m) => <option key={m} value={m} />)}</datalist>
      <Input data-testid={`${tid}-yil-bas-${i}`} type="number" min="1950" max="2100" placeholder="Yıl baş." className={kutu} value={a.yil_bas ?? a.yil ?? ""} onChange={(e) => set(i, "yil_bas", e.target.value === "" ? null : Number(e.target.value))} />
      <Input data-testid={`${tid}-yil-bit-${i}`} type="number" min="1950" max="2100" placeholder="Yıl bitiş" className={kutu} value={a.yil_bit ?? ""} onChange={(e) => set(i, "yil_bit", e.target.value === "" ? null : Number(e.target.value))} />
      <Input data-testid={`${tid}-motor-${i}`} placeholder="Motor" className={kutu} value={a.motor || ""} onChange={(e) => set(i, "motor", e.target.value)} />
      <button type="button" data-testid={`${tid}-sil-${i}`} onClick={sil} title="Aracı sil" className="justify-self-end p-1.5 rounded-lg text-muted-foreground hover:text-red-300 hover:bg-white/5"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
};

const AracListesi = ({ value, onChange, tid }) => {
  const o = useOneriler();
  const list = Array.isArray(value) ? value : [];
  const set = (i, k, v) => onChange(list.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-2 rounded-xl border border-white/10 p-3" data-testid={tid}>
      <datalist id={`${tid}-markalar`}>{o.markalar.map((m) => <option key={m} value={m} />)}</datalist>
      {list.length > 0 && <div className="hidden sm:grid grid-cols-[1.2fr_1.4fr_80px_80px_1fr_32px] gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground px-1"><span>Marka</span><span>Model</span><span>Yıl baş.</span><span>Yıl bitiş</span><span>Motor</span><span /></div>}
      {list.map((a, i) => <AracSatir key={i} a={a} i={i} tid={tid} set={set} o={o} sil={() => onChange(list.filter((_, j) => j !== i))} />)}
      {!list.length && <p className="text-xs text-muted-foreground">Bu ürünün takıldığı araç henüz girilmedi. Bayiler marka, model ve yılla aradığında bu liste kullanılır.</p>}
      <button type="button" data-testid={`${tid}-ekle`} onClick={() => onChange([...list, { marka: "", model: "", yil_bas: null, yil_bit: null, motor: "" }])}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-white/15 hover:bg-white/10 transition-colors"><Car className="w-3.5 h-3.5" />Araç ekle</button>
    </div>
  );
};

export const DynamicField = ({ def, value, onChange }) => {
  const tid = `oz-${def.kod}`;
  if (def.tip === "arac_listesi") return <AracListesi value={value} onChange={onChange} tid={tid} />;
  if (def.tip === "select") return <SelectBox testid={tid} value={value} onChange={onChange} options={def.secenekler || []} />;
  if (def.tip === "boolean") return <div className="h-10 flex items-center"><Switch data-testid={tid} checked={!!value} onCheckedChange={onChange} /></div>;
  return (
    <Input data-testid={tid} className="bg-white/5" type={def.tip === "number" ? "number" : def.tip === "date" ? "date" : "text"}
      value={value ?? ""} onChange={(e) => onChange(def.tip === "number" && e.target.value !== "" ? Number(e.target.value) : e.target.value)} />
  );
};
